import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiEye, FiFileText, FiPlus } from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import TableFilters, { SortableTh } from '../components/TableFilters';
import { TiposProcessoOptions } from '../constants/tiposProcessoLicenciamento';
import './Licencas.css';
import './ProcessosLicenciamento.css';

const STATUS = [
  ['', 'Todos'],
  ['preparacao', 'Preparação'],
  ['protocolado', 'Protocolado'],
  ['em_analise', 'Em análise'],
  ['pendencia', 'Pendência'],
  ['concluido', 'Concluído'],
  ['cancelado', 'Cancelado']
];
const vazio = { empreendimento_id: '', titulo: '', tipo: 'outro', orgao_ambiental: '', numero_processo: '', descricao: '', data_abertura: '', data_limite: '', status: 'preparacao' };
const statusLabel = (status) => STATUS.find(([value]) => value === status)?.[1] || status;
const formatDate = (date) => date ? new Date(`${String(date).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR') : '-';

export default function ProcessosLicenciamento() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [processos, setProcessos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [options, setOptions] = useState({ tipos: [], orgaos_ambientais: [] });
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(vazio);
  const [search, setSearch] = useState('');
  const [filterEmp, setFilterEmp] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterOrgao, setFilterOrgao] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAberturaDe, setFilterAberturaDe] = useState('');
  const [filterAberturaAte, setFilterAberturaAte] = useState('');
  const [filterLimiteDe, setFilterLimiteDe] = useState('');
  const [filterLimiteAte, setFilterLimiteAte] = useState('');
  const [sort, setSort] = useState('data_limite');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    Promise.all([
      api.get('/api/empreendimentos'),
      api.get('/api/processos-licenciamento/filtros')
    ]).then(([emp, filtros]) => {
      setEmpreendimentos(Array.isArray(emp.data) ? emp.data : (emp.data?.data || []));
      setOptions(filtros.data || { tipos: [], orgaos_ambientais: [] });
    }).catch(() => {});
  }, []);

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit, sort, order });
      if (search) params.set('q', search);
      if (filterEmp) params.set('empreendimento_id', filterEmp);
      if (filterTipo) params.set('tipo', filterTipo);
      if (filterOrgao) params.set('orgao_ambiental', filterOrgao);
      if (filterStatus) params.set('status', filterStatus);
      if (filterAberturaDe) params.set('abertura_de', filterAberturaDe);
      if (filterAberturaAte) params.set('abertura_ate', filterAberturaAte);
      if (filterLimiteDe) params.set('limite_de', filterLimiteDe);
      if (filterLimiteAte) params.set('limite_ate', filterLimiteAte);
      const res = await api.get(`/api/processos-licenciamento?${params}`);
      setProcessos(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao carregar processos de licenciamento');
    } finally {
      setLoading(false);
    }
  }, [search, filterEmp, filterTipo, filterOrgao, filterStatus, filterAberturaDe, filterAberturaAte, filterLimiteDe, filterLimiteAte, sort, order, page, limit]);

  useEffect(() => { carregar(); }, [carregar]);
  const ordenar = (column) => { setOrder((current) => sort === column && current === 'asc' ? 'desc' : 'asc'); setSort(column); setPage(1); };

  const criar = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/api/processos-licenciamento', form);
      setShowModal(false);
      setForm(vazio);
      navigate(`/processos-licenciamento/${res.data.id}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao criar processo de licenciamento');
    }
  };

  if (loading) return <div className="loading">Carregando...</div>;
  return (
    <div className="licencas-page processos-page">
      <div className="page-header">
        <h1 className="page-title"><FiFileText className="page-title-icon" /> Processos de licenciamento</h1>
        {hasPermission('license', 'create') && <button className="btn btn-primary" onClick={() => setShowModal(true)}><FiPlus /> Novo processo</button>}
      </div>
      <TableFilters search={search} onSearchChange={(value) => { setSearch(value); setPage(1); }} searchPlaceholder="Buscar por título, número, órgão ou empreendimento..."
        filterRows={[['search', 'empreendimento'], ['tipo', 'orgao', 'status'], ['abertura_de', 'abertura_ate', 'limite_de', 'limite_ate']]}
        filters={[
          { key: 'empreendimento', label: 'Empreendimento', value: filterEmp, onChange: (v) => { setFilterEmp(v); setPage(1); }, options: [{ value: '', label: 'Todos' }, ...empreendimentos.map((e) => ({ value: e.id, label: e.nome }))] },
          { key: 'tipo', label: 'Tipo', value: filterTipo, onChange: (v) => { setFilterTipo(v); setPage(1); }, options: [{ value: '', label: 'Todos' }, ...options.tipos.map((v) => ({ value: v, label: v }))] },
          { key: 'orgao', label: 'Órgão ambiental', value: filterOrgao, onChange: (v) => { setFilterOrgao(v); setPage(1); }, options: [{ value: '', label: 'Todos' }, ...options.orgaos_ambientais.map((v) => ({ value: v, label: v }))] },
          { key: 'status', label: 'Status', value: filterStatus, onChange: (v) => { setFilterStatus(v); setPage(1); }, options: STATUS.map(([value, label]) => ({ value, label })) },
          { key: 'abertura_de', label: 'Abertura de', type: 'date', value: filterAberturaDe, onChange: (v) => { setFilterAberturaDe(v); setPage(1); } },
          { key: 'abertura_ate', label: 'Abertura até', type: 'date', value: filterAberturaAte, onChange: (v) => { setFilterAberturaAte(v); setPage(1); } },
          { key: 'limite_de', label: 'Prazo de', type: 'date', value: filterLimiteDe, onChange: (v) => { setFilterLimiteDe(v); setPage(1); } },
          { key: 'limite_ate', label: 'Prazo até', type: 'date', value: filterLimiteAte, onChange: (v) => { setFilterLimiteAte(v); setPage(1); } }
        ]} total={total} page={page} limit={limit} onPageChange={setPage} onLimitChange={(value) => { setLimit(value); setPage(1); }} />
      {processos.length === 0 ? <div className="empty-state"><p>Nenhum processo de licenciamento encontrado.</p></div> : (
        <div className="table-container"><table className="data-table"><thead><tr>
          <SortableTh sortKey="empreendimento_nome" currentSort={sort} currentOrder={order} onSort={ordenar}>Empreendimento</SortableTh>
          <SortableTh sortKey="titulo" currentSort={sort} currentOrder={order} onSort={ordenar}>Processo</SortableTh>
          <SortableTh sortKey="tipo" currentSort={sort} currentOrder={order} onSort={ordenar}>Tipo</SortableTh>
          <SortableTh sortKey="orgao_ambiental" currentSort={sort} currentOrder={order} onSort={ordenar}>Órgão</SortableTh>
          <SortableTh sortKey="data_limite" currentSort={sort} currentOrder={order} onSort={ordenar}>Prazo</SortableTh>
          <SortableTh sortKey="status" currentSort={sort} currentOrder={order} onSort={ordenar}>Status</SortableTh>
          <th>Vínculos</th><th>Ações</th>
        </tr></thead><tbody>{processos.map((p) => <tr key={p.id}>
          <td><Link className="link-detalhes" to={`/empreendimentos/${p.empreendimento_id}`}>{p.empreendimento_nome}</Link></td>
          <td><Link className="licenca-numero-link" to={`/processos-licenciamento/${p.id}`}>{p.titulo}</Link><small className="processo-numero">{p.numero_processo || 'Número a informar'}</small></td>
          <td>{p.tipo || '-'}</td><td>{p.orgao_ambiental || '-'}</td><td>{formatDate(p.data_limite)}</td>
          <td><span className={`badge processo-badge status-${p.status}`}>{statusLabel(p.status)}</span></td>
          <td>{p.documentos_count} doc. · {p.tarefas_count} prazo(s){p.licenca_id && <><br /><Link to={`/licencas/${p.licenca_id}/validar`}>Licença emitida</Link></>}</td>
          <td><Link className="btn btn-sm btn-primary btn-icon" title="Visualizar detalhes" to={`/processos-licenciamento/${p.id}`}><FiEye /></Link></td>
        </tr>)}</tbody></table></div>
      )}
      {showModal && <div className="modal-overlay" onClick={() => setShowModal(false)}><div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2><FiPlus /> Novo processo de licenciamento</h2><form onSubmit={criar}>
          <div className="form-group"><label>Empreendimento *</label><select required value={form.empreendimento_id} onChange={(e) => setForm({ ...form, empreendimento_id: e.target.value })}><option value="">Selecione</option>{empreendimentos.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}</select></div>
          <div className="form-group"><label>Título *</label><input required value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} /></div>
          <div className="form-row"><div className="form-group"><label>Tipo</label><select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>{TiposProcessoOptions()}</select></div><div className="form-group"><label>Órgão ambiental</label><input value={form.orgao_ambiental} onChange={(e) => setForm({ ...form, orgao_ambiental: e.target.value })} /></div></div>
          <div className="form-row"><div className="form-group"><label>Número do processo</label><input value={form.numero_processo} onChange={(e) => setForm({ ...form, numero_processo: e.target.value })} /></div><div className="form-group"><label>Data de abertura</label><input type="date" value={form.data_abertura} onChange={(e) => setForm({ ...form, data_abertura: e.target.value })} /></div></div>
          <div className="form-group"><label>Prazo</label><input type="date" value={form.data_limite} onChange={(e) => setForm({ ...form, data_limite: e.target.value })} /></div>
          <div className="form-group"><label>Descrição</label><textarea rows="3" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} /></div>
          <div className="form-actions"><button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button><button className="btn btn-primary">Criar processo</button></div>
        </form>
      </div></div>}
    </div>
  );
}
